import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProfileSidebar from "./components/sidebar/ProfileSidebar";
import ChangePassword from "./components/ChangePassword";
import Profile from "./components/Profile"; // Import the Profile component
import Integrations from "./components/Integrations";
import ApiServices from "./components/ApiServices";
import FileManagement from "./components/filemanagement/FileManagement";
import Usage from "./components/Usage";
import QueryAnalyzer from "./components/QueryAnalyzer";
import ProactiveSettings from "./components/ProactiveSettings";
import VoiceChatHistory from "./components/VoiceChatHistory";
import KnowledgeBase from "./components/KnowledgeBase";
import SecondBrain from "./components/SecondBrain";
import Contacts from "./components/Contacts";
import BillingPage from "./components/billing/BillingPage";
import UsageDetails from "./components/billing/UsageDetails";
import { supabase } from "./supabaseClient";
import Navbar from "./components/Navbar";
import ConfirmationPopup from "./components/common/ConfirmationPopup";

function UserProfile({ toggleTheme, isDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(location.state?.activeMenu || 1);
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showVoiceChatHistory, setShowVoiceChatHistory] = useState(false);
  const [billingMessage, setBillingMessage] = useState(null);

  // Fetch user on mount - ensures userId is available for all pages on refresh
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUserId(session.user.id);
          setUserEmail(session.user.email);
        }
      } catch (err) {
        console.error("Error fetching user session:", err);
      }
    };
    fetchUser();
  }, []);

  // Update activeMenu when navigating from another page with state
  useEffect(() => {
    if (location.state?.activeMenu) {
      setActiveMenu(location.state.activeMenu);
    }
  }, [location.state]);

  // Handle Stripe checkout redirect (?billing=success or ?billing=cancel)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const billingStatus = params.get("billing");
    if (billingStatus === "success") {
      setActiveMenu(15);
      setBillingMessage({ text: "Subscription successful! Your credits are being activated.", type: "success" });
      window.history.replaceState({}, "", "/userprofile");
    } else if (billingStatus === "cancel") {
      setActiveMenu(15);
      setBillingMessage({ text: "Checkout was cancelled.", type: "info" });
      window.history.replaceState({}, "", "/userprofile");
    }

    const topup = params.get("topup");
    if (topup === "true") {
      setActiveMenu(15);
      setBillingMessage({ text: "Credits added successfully!", type: "success" });
      window.history.replaceState({}, "", "/userprofile");
    }
  }, [location.search]);

  const handleMenuChange = (menuId) => {
    // Handle logout case separately (now id 8)
    if (menuId === 8) {
      setShowLogoutModal(true);
      return;
    }
    // Handle voice chat history (id 10) - open modal instead of changing content
    if (menuId === 10) {
      setShowVoiceChatHistory(true);
      return;
    }
    setActiveMenu(menuId);
    // console.log("Menu changed to:", menuId); // Debug log
  };

  // Callback to receive user info from Profile component
  const handleUserInfoFetch = (id, email) => {
    setUserId(id);
    if (email) setUserEmail(email);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    const { data } = await supabase.auth.getUser();
    const { error } = await supabase.auth.signOut();
    setShowLogoutModal(false);
    navigate("/login");
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const renderContent = () => {
    console.log("Rendering content for:", activeMenu); // Debug log

    switch (activeMenu) {
      case 1:
        return <Profile onUserIdFetch={handleUserInfoFetch} />;
      case 2:
        if (!userId) return <div className="settings-page-container"><div className="settings-loading"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div></div>;
        return <Integrations embedded={true} userId={userId} toggleTheme={toggleTheme} isDarkMode={isDarkMode} />;
      case 3:
        return <ApiServices />;
      case 4:
        return <ChangePassword />;
      case 5:
        return <FileManagement />;
      case 6:
        if (!userId) return <div className="settings-page-container"><div className="settings-loading"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div></div>;
        return <Usage userId={userId} userEmail={userEmail} />;
      case 9:
        if (!userId) return <div>Loading user info...</div>;
        return <ProactiveSettings userId={userId} />;
      case 11:
        if (!userId) return <div>Loading user info...</div>;
        return <KnowledgeBase userId={userId} />;
      case 12:
        if (!userId) return <div>Loading user info...</div>;
        return <SecondBrain userId={userId} />;
      case 13:
        if (!userId) return <div className="settings-page-container"><div className="settings-loading"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div></div>;
        return <Contacts userId={userId} />;
      case 14:
        if (!userId) return <div className="settings-page-container"><div className="settings-loading"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div></div>;
        return <QueryAnalyzer userId={userId} />;
      case 15:
        if (!userId) return <div className="settings-page-container"><div className="settings-loading"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div></div>;
        return <BillingPage userId={userId} isDarkMode={isDarkMode} initialToast={billingMessage} onToastShown={() => setBillingMessage(null)} />;
      case 16:
        if (!userId) return <div className="settings-page-container"><div className="settings-loading"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div></div>;
        return <UsageDetails userId={userId} isDarkMode={isDarkMode} />;
      // Case 7 (Dark Theme) is a toggle, Case 8 (logout) is handled in handleMenuChange
      default:
        return <Profile onUserIdFetch={handleUserInfoFetch} />;
    }
  };

  // Get current menu label for header
  const getMenuLabel = () => {
    const menuLabels = {
      1: { title: "Profile", subtitle: "Manage your personal information" },
      2: { title: "Integrations", subtitle: "Connect your favorite apps" },
      4: { title: "Change Password", subtitle: "Update your security settings" },
      6: { title: "Usage Analytics", subtitle: "View your API usage statistics" },
      14: { title: "Query Analyzer", subtitle: "Smart vs Simple mode analysis per query" },
      15: { title: "Billing & Credits", subtitle: "Manage your AI credits and plans" },
      16: { title: "Credit Usage", subtitle: "See how your credits are being used" },
      11: { title: "Knowledge Base", subtitle: "Manage what Zoomey remembers" },
      12: { title: "Second Brain", subtitle: "Organize with PARA method" },
      13: { title: "Contacts", subtitle: "Manage your contacts" },
    };
    return menuLabels[activeMenu] || { title: "Settings", subtitle: "Manage your account" };
  };

  const currentMenu = getMenuLabel();

  return (
    <>
      <div
        className="d-flex"
        style={{ minHeight: "100vh", background: "var(--chatbot-bg)" }}
      >
        {/* Sidebar */}
        <div
          style={{
            position: "fixed",
            height: "100vh",
            zIndex: 10,
          }}
        >
          <ProfileSidebar
            onMenuChange={handleMenuChange}
            activeMenu={activeMenu}
            toggleTheme={toggleTheme}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Main Content Area */}
        <div
          className="flex-grow-1"
          style={{
            background: "var(--chatbot-bg)",
            marginLeft: "260px",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Top Header Bar */}
          <div
            style={{
              background: "var(--background)",
              borderBottom: "1px solid var(--border-color)",
              padding: "20px 32px",
              flexShrink: 0,
              height: "81px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div className="d-flex align-items-center justify-content-between w-100">
              <div className="d-flex align-items-center gap-3">
                {/* Logo */}
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, var(--orange-bg), #ff7b00)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(250, 84, 0, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => navigate("/chat-bot")}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(250, 84, 0, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(250, 84, 0, 0.3)";
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: "18px",
                      userSelect: "none",
                    }}
                  >
                    Z
                  </span>
                </div>
                {/* Title */}
                <div>
                  <h6
                    style={{
                      margin: 0,
                      color: "var(--text)",
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    {currentMenu.title}
                  </h6>
                  <span
                    style={{
                      color: "var(--text)",
                      opacity: 0.5,
                      fontSize: "12px",
                    }}
                  >
                    {currentMenu.subtitle}
                  </span>
                </div>
              </div>

              {/* Right side - Back to Chat button */}
              <button
                onClick={() => navigate("/chat-bot")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  border: "1px solid var(--border-color)",
                  background: "var(--background)",
                  color: "var(--text)",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--teal-bg)";
                  e.currentTarget.style.color = "white";
                  e.currentTarget.style.borderColor = "var(--teal-bg)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--background)";
                  e.currentTarget.style.color = "var(--text)";
                  e.currentTarget.style.borderColor = "var(--border-color)";
                }}
              >
                <i className="bi bi-chat-dots-fill"></i>
                Back to Chat
              </button>
            </div>
          </div>

          {/* Content - Scrollable Area */}
          <div
            className="profile-scroll-content"
            style={{
              padding: "12px 12px 12px 22px",
              flex: 1,
              overflowY: "auto",
            }}
          >
            {/* Content Card */}
            <div
              style={{
                background: "var(--background)",
                borderRadius: "16px",
                border: "1px solid var(--border-color)",
                minHeight: "calc(100vh - 140px)",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
              }}
            >
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationPopup
        show={showLogoutModal}
        handleClose={handleLogoutCancel}
        handleConfirm={handleLogout}
        title="Do you really want to logout?"
        body=" You will need to sign in again to access your account."
      />

      {/* Voice Chat History Modal */}
      <VoiceChatHistory
        isOpen={showVoiceChatHistory}
        onClose={() => setShowVoiceChatHistory(false)}
      />
    </>
  );
}

export default UserProfile;
